import React, { useState, MouseEvent, JSX, Suspense, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, A11y, Autoplay } from 'swiper/modules';

import { ApiImage, ApiMedia } from '@ibe/api';
import useMediaQuery, { MEDIAQUERY_DEFAULTS, QueryOpts } from '../../../Hooks/useMediaQuery';
import { getImageUrl } from '../../../Modules/Camper/externals';
import classNames from 'classnames';
import { randomId } from '../../../../Utils/globals';

const YouTube = React.lazy(() => import('react-youtube'));

export interface Props {
  images?: string[];
  videos?: string[];
  placeholderImage: string;
  fixedRatio?: {
    x: number;
    y: number;
    mediaQueryOptions?: QueryOpts;
  };
  mobileControls?: boolean;
  smallControls?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  rawComponents?: JSX.Element[];
  checkProtocol?: boolean;
  containImage?: boolean;
  buttonContentRight?: React.ReactNode;
  buttonContentLeft?: React.ReactNode;
  organicArrows?: boolean;
}

export const Slider = (props: Props): JSX.Element => {
  const {
    images,
    videos,
    placeholderImage,
    fixedRatio,
    mobileControls,
    smallControls,
    autoplay = true,
    autoplayInterval,
    rawComponents,
    containImage,
    buttonContentRight,
    buttonContentLeft
  } = props;

  const [autoPlay, setAutoPlay] = useState(autoplay);
  const [, forceUpdate] = useState<number>(0);

  const isBreakpoint = useMediaQuery({
    type: fixedRatio?.mediaQueryOptions?.type || 'min',
    query: fixedRatio?.mediaQueryOptions?.query || MEDIAQUERY_DEFAULTS.xs
  });

  const sliderIdLeft = randomId(15);
  const sliderIdRight = randomId(15);

  useEffect(() => {
    forceUpdate(value => value + 1);
  }, []);

  const setAutoPlayFalse = (): void => {
    setAutoPlay(false);
  };

  function getYoutubeId(src: string): string {
    let id = '';
    const url = src.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
    if (url[2] !== undefined) {
      const splitUrl = url[2].split(/[^0-9a-z_-]/i);
      id = splitUrl[0];
    } else {
      id = src;
    }
    return id;
  }

  let medias: { item: JSX.Element; key: string }[] = [];
  if (images?.length) {
    const imageDivs = images.map(src => {
      return {
        item: (
          <div
            key={src}
            style={{ backgroundImage: `url("${src}")` }}
            className={containImage ? 'slider__screenImageContain' : 'slider__screenImage'}
          />
        ),
        key: src
      };
    });
    medias = medias.concat(imageDivs);
  }
  if (videos?.length) {
    const mp4Videos = videos
      .filter(v => v.includes('.mp4'))
      .map(video => ({
        item: (
          <div key={video} className="slider__screen">
            <video width="100%" height="100%" controls onPlay={(): void => setAutoPlayFalse()}>
              <source src={video} type="video/mp4" style={{ width: '100%', height: '100%' }} />
            </video>
          </div>
        ),
        key: video
      }));
    const youtubeVideosId = videos
      .filter(v => v.includes('youtu'))
      .map(video => ({
        item: (
          <div key={video} className="slider__screen">
            <YouTube
              videoId={getYoutubeId(video)}
              onPlay={(): void => setAutoPlayFalse()}
              opts={{ width: '100%', height: '100%' }}
            />
          </div>
        ),
        key: video
      }));
    medias = medias.concat(mp4Videos);
    medias = medias.concat(youtubeVideosId);
  }
  if (!medias.length) {
    medias.push({
      item: (
        <div
          key={0}
          style={{ backgroundImage: `url("${placeholderImage}")` }}
          className="slider__screenImage"
        />
      ),
      key: '0'
    });
  }

  if (rawComponents?.length) {
    medias = rawComponents.map((rawComponent, idx: number) => ({
      item: rawComponent,
      key: idx.toString()
    }));
  }

  return (
    <div
      className={`slider__layoutContainer${
        fixedRatio && isBreakpoint ? ' slider__layoutContainer--fixed' : ''
      }${mobileControls ? ' slider__layoutContainer--mobile-controls' : ''}${
        smallControls ? ' slider__layoutContainer--small-controls' : ''
      }`}
      onClick={(e: MouseEvent<HTMLDivElement>): void => e.stopPropagation()}
    >
      <Suspense fallback={<div>Loading slider…</div>}>
        <div className="slider__layoutContainer__inner">
          <Swiper
            modules={[Navigation, Keyboard, A11y, Autoplay]}
            navigation={{ prevEl: `.${sliderIdLeft}-left`, nextEl: `.${sliderIdRight}-right` }}
            autoplay={
              autoPlay
                ? {
                    delay: autoplayInterval ?? 3000,
                    disableOnInteraction: true
                  }
                : undefined
            }
            loop
          >
            {medias &&
              medias.length > 0 &&
              medias.map(media => (
                <SwiperSlide key={media.key} className="slider__screen">
                  <div>{media.item}</div>
                </SwiperSlide>
              ))}
          </Swiper>
          {buttonContentLeft && (
            <div
              className={classNames(
                'slider__button',
                'slider__button--left',
                `${sliderIdLeft}-left`
              )}
            >
              {buttonContentLeft}
            </div>
          )}
          {buttonContentRight && (
            <div
              className={classNames(
                'slider__button',
                'slider__button--right',
                `${sliderIdRight}-right`
              )}
            >
              {buttonContentRight}
            </div>
          )}
        </div>
      </Suspense>
    </div>
  );
};

export function withMedia(
  SliderComponent: (props: Props) => JSX.Element,
  placeholderImage: string,
  media: ApiMedia | undefined,
  props?: Partial<Props>,
  imageSize?: 'small' | 'large'
): JSX.Element {
  return SliderComponent({
    ...splitMediaIntoImagesAndVideosUrls(media, imageSize, props?.checkProtocol),
    placeholderImage,
    ...props
  });
}

export function splitMediaIntoImagesAndVideosUrls(
  props?: ApiMedia,
  imageSize?: 'small' | 'large',
  checkProtocol?: boolean
): { images: string[]; videos: string[] } {
  const images: string[] = [];
  const videos: string[] = [];
  if (props) {
    let mainImg: string | undefined;
    if (props.mainImage) {
      mainImg = props.mainImage?.large?.url || props.mainImage?.small?.url;
      if (checkProtocol) {
        mainImg = getImageUrl(mainImg);
      }
    }
    props.images.forEach(img => {
      if ((!imageSize || imageSize === 'large') && img.large && img.large.url) {
        images.push(checkProtocol ? (getImageUrl(img.large.url) as string) : img.large.url);
      } else if (imageSize === 'small' && img.small && img.small.url) {
        images.push(checkProtocol ? (getImageUrl(img.small.url) as string) : img.small.url);
      }
    });
    if (mainImg) {
      images.unshift(mainImg);
    }
    props.videos.forEach(video => {
      if (video.url) {
        videos.push(video.url);
      }
    });
  }
  return { images, videos };
}

export function splitMediaIntoImagesAndVideos(
  props?: ApiMedia,
  imageSize?: 'small' | 'large'
): { images: ApiImage[]; videos: string[] } {
  const images: ApiImage[] = [];
  const videos: string[] = [];
  if (props) {
    let mainImg: ApiImage | undefined;
    if (props.mainImage) {
      mainImg = props.mainImage?.large || props.mainImage?.small;
    }
    props.images.forEach(img => {
      if ((!imageSize || imageSize === 'large') && img.large && img.large.url) {
        images.push(img.large);
      } else if (imageSize === 'small' && img.small && img.small.url) {
        images.push(img.small);
      }
    });
    if (mainImg) {
      images.unshift(mainImg);
    }
    props.videos.forEach(video => {
      if (video.url) {
        videos.push(video.url);
      }
    });
  }
  return { images, videos };
}
